import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { addJob, getJobById, updateJob } from "@endpoints/job.service";
import { JobStatus } from "@utils/constants";
import {
  getFirstInvalidStep,
  JOB_FORM_DEFAULTS,
  JobFormValues,
  STEP_COUNT,
  STEP_SCHEMAS,
} from "../schema/jobForm.schema";
import { fromJob, toJobPayload } from "../lib/jobPayload";

const clampStep = (step: number) =>
  Math.min(Math.max(Math.trunc(step) || 1, 1), STEP_COUNT);

export const useCreateJobWizard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  // Captured once so a refresh resumes the saved draft at the same step.
  const initialParams = useRef({
    jobId: searchParams.get("jobId"),
    step: clampStep(Number(searchParams.get("step"))),
  });

  const [activeStep, setActiveStep] = useState(1);
  const [jobId, setJobId] = useState<string | null>(
    initialParams.current.jobId,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(
    Boolean(initialParams.current.jobId),
  );

  // Read by the resolver, and updated synchronously in goToStep so a
  // validation run right after a step change uses the new step's schema.
  const stepRef = useRef(activeStep);

  const stepResolver: Resolver<JobFormValues> = (values, context, options) =>
    zodResolver(STEP_SCHEMAS[stepRef.current - 1] as any, undefined, {
      raw: true,
    })(values, context, options as any) as any;

  const form = useForm<JobFormValues>({
    defaultValues: JOB_FORM_DEFAULTS,
    mode: "onTouched",
    resolver: stepResolver,
  });

  const goToStep = useCallback((step: number) => {
    stepRef.current = step;
    setActiveStep(step);
  }, []);

  useEffect(() => {
    if (isLoadingDraft || !jobId) return;
    setSearchParams({ jobId, step: String(activeStep) }, { replace: true });
  }, [jobId, activeStep, isLoadingDraft, setSearchParams]);

  useEffect(() => {
    const { jobId: draftId, step } = initialParams.current;
    if (!draftId) return;
    let isCancelled = false;

    getJobById({ jobId: draftId })
      .then(({ data }) => {
        if (isCancelled) return;
        const values = fromJob(data);
        form.reset(values);
        goToStep(Math.min(step, getFirstInvalidStep(values) ?? STEP_COUNT));
      })
      .catch((error) => {
        if (!isCancelled) enqueueSnackbar(String(error), { variant: "error" });
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingDraft(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [form, goToStep, enqueueSnackbar]);

  const persistDraft = async (values: JobFormValues) => {
    const payload = { ...toJobPayload(values), status: JobStatus.DRAFT };

    if (jobId) {
      if (activeStep === 2) {
        delete payload.keyResponsibilities;
        delete payload.salaryAndBenefits;
      }
      const { success } = await updateJob({ jobId, ...payload });
      if (!success) throw new Error("Could not save the job draft");
      return;
    }

    const { success, data } = await addJob(payload);
    if (!success || !data?._id) throw new Error("Could not save the job draft");
    setJobId(data._id);
  };

  const withSaving = async (action: () => Promise<void>) => {
    try {
      setIsSaving(true);
      await action();
    } catch (error) {
      enqueueSnackbar(error?.message ?? String(error), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Moves forward only when every step before `target` is valid. The current
  // step is validated by handleSubmit so its errors render inline; earlier
  // steps are re-checked because the user may have edited them since.
  const goForwardTo = (target: number) =>
    form.handleSubmit((values) => {
      const invalidStep = getFirstInvalidStep(values, target - 1);
      if (invalidStep) {
        goToStep(invalidStep);
        form.trigger();
        return;
      }
      return withSaving(async () => {
        await persistDraft(values);
        goToStep(target);
      });
    })();

  const continueToNextStep = () => goForwardTo(activeStep + 1);

  const goBack = () => {
    if (activeStep === 1) navigate(-1);
    else goToStep(activeStep - 1);
  };

  const requestStep = (target: number) => {
    if (isSaving || target === activeStep) return;
    if (target < activeStep) goToStep(target);
    else goForwardTo(target);
  };

  // No validation: the backend accepts partial drafts.
  const saveDraft = () =>
    withSaving(async () => {
      await persistDraft(form.getValues());
      enqueueSnackbar("Draft saved", { variant: "success" });
    });

  const publishJob = () =>
    form.handleSubmit((values) => {
      const invalidStep = getFirstInvalidStep(values);
      if (invalidStep) {
        goToStep(invalidStep);
        form.trigger();
        return;
      }
      return withSaving(async () => {
        await persistDraft(values);
        // TODO: call the payment + publish endpoint once it exists.
        enqueueSnackbar(
          "Job saved as a draft. Payment and publishing are not connected yet.",
          { variant: "info" },
        );
      });
    })();

  return {
    form,
    jobId,
    activeStep,
    isSaving,
    isLoadingDraft,
    continueToNextStep,
    goBack,
    requestStep,
    saveDraft,
    publishJob,
  };
};

export type CreateJobWizard = Omit<
  ReturnType<typeof useCreateJobWizard>,
  "form"
>;

export const CreateJobContext = createContext<CreateJobWizard | null>(null);

export const useCreateJob = () => {
  const context = useContext(CreateJobContext);
  if (!context) {
    throw new Error("useCreateJob must be used inside CreateJobPage");
  }
  return context;
};
